// hooks/useHiddenPostsList.ts — manage-list hook for Settings → Content
// Preferences → Hidden posts.
//
// Loads the signed-in user's "Not interested" posts and exposes `unhide`,
// which reuses postsService.unhidePost — the same call PostMoreMenu's Undo
// toast makes, minus the feed-store injection (see `restoreToFeed: false`
// below — this list must never drop a possibly-days-old post into the live
// home feed). Components must NOT import postsService directly.

import { useCallback, useEffect, useRef, useState } from 'react';
import { postsService } from '@/core/services/postsService';
import type { Post } from '@/core/models/post';

/**
 * 'unhidden' — the post is genuinely unhidden; the row is removed.
 * 'stale'    — the toggle landed on an already-unhidden post and re-hid it
 *              (another tab, a stale row); the row stays and the whole list
 *              is refetched so it reflects reality.
 * 'error'    — the request itself failed; the row stays untouched.
 * 'busy'     — a request for this post was already in flight; this call was
 *              a no-op (the double-click/tap guard).
 */
export type UnhideOutcome = 'unhidden' | 'stale' | 'error' | 'busy';

export interface HiddenPostsListHook {
  posts: Post[];
  loading: boolean;
  error: boolean;
  /** Post ids currently mid-request — used to disable a row's button so a
   *  double-click can't re-hide the post it just unhid. */
  pending: Set<string>;
  unhide: (post: Post) => Promise<UnhideOutcome>;
  /** Re-runs the list fetch — the error state's "Try again" action, and also
   *  called internally when `unhide` discovers a stale row. */
  refresh: () => void;
}

export function useHiddenPostsList(): HiddenPostsListHook {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [tick, setTick] = useState(0);
  // The actual concurrency guard — see useMutedAccountsList's identical
  // comment: React state can't tell two same-tick clicks apart, a ref can.
  const pendingRef = useRef<Set<string>>(new Set());

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    // Unlike a mount-only effect, this one can refire (refresh()/tick), so it
    // DOES need to reset loading/error itself — the initial state alone
    // isn't enough past the first run.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flags, see above
    setLoading(true);
    setError(false);
    postsService
      .hiddenPosts()
      .then((rows) => {
        if (!cancelled) setPosts(rows);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const unhide = useCallback(
    async (post: Post): Promise<UnhideOutcome> => {
      if (pendingRef.current.has(post.id)) return 'busy';
      pendingRef.current.add(post.id);
      setPending((s) => new Set(s).add(post.id));
      try {
        const hidden = await postsService.unhidePost(post, { restoreToFeed: false });
        if (hidden === false) {
          setPosts((rows) => rows.filter((p) => p.id !== post.id));
          return 'unhidden';
        }
        if (hidden === true) {
          // Stale: something else already unhid this post and our toggle just
          // re-hid it. The local row is now lying — refetch instead of
          // patching around it.
          refresh();
          return 'stale';
        }
        return 'error';
      } finally {
        pendingRef.current.delete(post.id);
        setPending((s) => {
          const next = new Set(s);
          next.delete(post.id);
          return next;
        });
      }
    },
    [refresh],
  );

  return { posts, loading, error, pending, unhide, refresh };
}
